import React, { useEffect, useState } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
    Modal,
    Button,
    Grid,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { ResourceLinks } from "../../../../constants";

const CustomAccordion = styled(Accordion)(({ theme }) => ({
    "&:before": {
        display: "none",
    },
    "&.MuiAccordion-root.Mui-expanded": {
        marginTop: 0,
        marginBottom: 0,
    },
}));

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: "rgb(217, 217, 217)",
    borderBottom: "none",
    "&.Mui-expanded": {
        backgroundColor: "rgb(0, 176, 240)",
        borderBottom: "10px solid rgb(0, 133, 180)",
    },
}));

export default function WebSelectScenarioInfo({ question, onAnswer = () => {} }) {
    const questionData = question;
    const imagesResources = ResourceLinks.llnQuizImage;

    const [expanded, setExpanded] = useState("panel1");
    const [modalOpen, setModalOpen] = useState(false);
    const [modalContent, setModalContent] = useState(null);
    const [answerState, setAnswerState] = useState(
        questionData?.answer?.type === "keyvaluepairs"
            ? Object.keys(questionData.answer.value).reduce((acc, key) => {
                  acc[key] = "";
                  return acc;
              }, {})
            : {}
    );

    // every time question change we will set to reset needed to reset
    useEffect(() => {
        setExpanded("panel1");
        setModalOpen(false);
        setModalContent(null);
        setAnswerState(questionData?.answer?.type === "keyvaluepairs"
            ? Object.keys(questionData.answer.value).reduce((acc, key) => {
                  acc[key] = "";
                  return acc;
              }, {})
            : {});
    }, [question,
        questionData?.answer?.type,
        questionData.answer.value,
    ]);

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleImageClick = (course) => {
        setModalContent(course);
        setModalOpen(true);
    };

    const handleClose = () => {
        setModalOpen(false);
    };

    const handleSelection = (type, value) => {
        setAnswerState((prevState) => {
            const updatedState = { ...prevState, [type]: value };

            let canProceed = true;
            onAnswer({
                canProceed,
                answer: updatedState,
            });

            return updatedState;
        });
    };

    return (
        <Box sx={{ maxWidth: 1100, margin: "0 auto", padding: "20px" }}>
            {/* Scenario Accordion */}
            <CustomAccordion
                expanded={expanded === "panel1"}
                onChange={handleChange("panel1")}
            >
                <CustomAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="scenario-content"
                    id="scenario-header"
                >
                    <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
                        {questionData.data.scenario.title}
                    </Typography>
                </CustomAccordionSummary>
                <AccordionDetails>
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-around"
                        borderTop="10px solid rgb(191, 191, 191)"
                    >
                        {/* Content for Scenario */}
                        <Box flexBasis="75%">
                            <Typography
                                sx={{ fontSize: "20px", margin: "12px" }}
                            >
                                {questionData.data.scenario.paragraph1}
                            </Typography>
                            <Typography
                                sx={{ fontSize: "20px", margin: "12px" }}
                            >
                                {questionData.data.scenario.paragraph2}
                            </Typography>
                            <Typography
                                sx={{ fontSize: "20px", margin: "12px" }}
                            >
                                {questionData.data.scenario.paragraph3}
                            </Typography>
                        </Box>

                        {/* Image for Scenario */}
                        <Box flexBasis="25%">
                            <Box component="img"
                                src={`${imagesResources}${questionData.data.scenario.image.src}`}
                                alt={questionData.data.scenario.image.alt}
                                sx={{ width: "100%" }}
                            />
                        </Box>
                    </Box>
                </AccordionDetails>
            </CustomAccordion>

            {/* Course Information Accordion */}
            <CustomAccordion
                expanded={expanded === "panel2"}
                onChange={handleChange("panel2")}
            >
                <CustomAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="courseInfo-content"
                    id="courseInfo-header"
                >
                    <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
                        {questionData.data.courseInformation?.title
                            ? questionData.data.courseInformation.title
                            : questionData.data.bikeReviews?.title}
                    </Typography>
                </CustomAccordionSummary>
                <AccordionDetails>
                    <Typography
                        sx={{
                            fontSize: "32px",
                            fontWeight: "700",
                            textAlign: "center",
                            marginBottom: "16px",
                            borderTop: "10px solid rgb(191, 191, 191)",
                            overflowY: "auto",
                        }}
                    >
                        {questionData.data.courseInformation &&
                            "Click on each course for more information."}
                    </Typography>

                    {questionData.data.courseInformation && (
                        <Box
                            display="flex"
                            flexDirection="row"
                            flexWrap="wrap"
                            alignItems="center"
                            justifyContent="space-around"
                        >
                            {questionData.data.courseInformation.courses.map(
                                (course, index) => (
                                    <Box
                                        key={index}
                                        textAlign="center"
                                        flex="1 1 30%"
                                        m={1}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: "24px",
                                                fontWeight: "700",
                                                marginBottom: "10px",
                                            }}
                                        >
                                            {course.title}
                                        </Typography>
                                        <Box component="img"
                                            src={`${imagesResources}${course.image.src}`}
                                            alt={course.image.alt}
                                            sx={{
                                                height: "245px",
                                                objectFit: "contain",
                                            }}
                                            onClick={() =>
                                                handleImageClick(course)
                                            }
                                        />
                                    </Box>
                                )
                            )}
                        </Box>
                    )}
                    <Grid
                        container
                        alignItems="stretch"
                        sx={{
                            overflowY: "auto",
                        }}
                    >
                        {questionData.data.bikeReviews?.reviews.map(
                            (review, index) => (
                                <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    key={index}
                                    style={{ display: "flex" }}
                                >
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        border="2px solid #767171"
                                        padding="10px"
                                        margin="10px"
                                        flexGrow={1}
                                    >
                                        <Box display="flex" alignItems="center">
                                            <Typography
                                                variant="h6"
                                                sx={{
                                                    color: "#0070BF",
                                                    marginRight: "8px",
                                                    fontWeight: "700",
                                                }}
                                            >
                                                {review.title}
                                            </Typography>
                                            <Box
                                                display="flex"
                                                alignItems="center"
                                            >
                                                <Box component="img"
                                                    src={`${imagesResources}${review.rating}`}
                                                    alt={`${review.title} rating`}
                                                    sx={{
                                                        width: "80px",
                                                    }}
                                                />
                                            </Box>
                                        </Box>

                                        <Box marginTop="8px">
                                            <Typography
                                                variant="subtitle1"
                                                sx={{ fontWeight: "bold" }}
                                            >
                                                {
                                                    review.review.split(
                                                        /[.!?]\s/
                                                    )[0]
                                                }
                                                {
                                                    review.review.match(
                                                        /[.!?]/
                                                    )[0]
                                                }
                                            </Typography>
                                            <Typography lineHeight={1}>
                                                {review.review.substring(
                                                    review.review.indexOf(
                                                        review.review.split(
                                                            /[.!?]\s/
                                                        )[0]
                                                    ) +
                                                        review.review.split(
                                                            /[.!?]\s/
                                                        )[0].length +
                                                        2
                                                )}
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            )
                        )}
                    </Grid>
                </AccordionDetails>
            </CustomAccordion>

            {/* Answer Accordion */}
            <CustomAccordion
                expanded={expanded === "panel3"}
                onChange={handleChange("panel3")}
            >
                <CustomAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="answer-content"
                    id="answer-header"
                >
                    <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
                        {questionData.data.answerSection.title}
                    </Typography>
                </CustomAccordionSummary>
                <AccordionDetails>
                    <Box
                        display="flex"
                        flexDirection="row"
                        flexWrap="wrap"
                        alignItems="center"
                        justifyContent="space-around"
                        borderTop="10px solid rgb(191, 191, 191)"
                    >
                        {questionData.data.answerSection.options.map(
                            (option, index) => (
                                <Box
                                    key={index}
                                    textAlign="center"
                                    flex="1 1 30%"
                                    m={1}
                                    display="flex"
                                    flexDirection="column"
                                    borderBottom="10px solid rgb(0, 132, 180)"
                                    backgroundColor="rgb(242, 242, 242)"
                                    alignItems="center"
                                    maxWidth={295}
                                    minWidth={210}
                                >
                                    <Box
                                        sx={{
                                            textAlign: "center",
                                            fontWeight: "bold",
                                            fontSize: "20px",
                                            backgroundColor: "rgb(0, 176, 240)",
                                            color: "black",
                                            borderBottom:
                                                "10px solid rgb(0, 132, 180)",
                                            paddingY: "8px",
                                            width: "100%",
                                            minWidth: "210px",
                                        }}
                                    >
                                        {option.header}
                                    </Box>
                                    {option.buttons.map((button, idx) => (
                                        <Button
                                            key={idx}
                                            variant="contained"
                                            sx={{
                                                minWidth: "210px",
                                                color: "black",
                                                fontSize: "22px",
                                                margin: 1,
                                                backgroundColor:
                                                    answerState[
                                                        option.value
                                                    ] === button.label
                                                        ? "rgb(175, 226, 62)"
                                                        : "rgb(218, 218, 218)",
                                                borderBottom:
                                                    answerState[
                                                        option.value
                                                    ] === button.label
                                                        ? "10px solid rgb(99, 151, 41)"
                                                        : "10px solid rgb(128, 128, 128)",
                                                height: "50px",
                                                fontWeight: "bold",
                                                cursor: "pointer",
                                                userSelect: "none",
                                                borderRadius: "0px",
                                                "&:hover": {
                                                    backgroundColor:
                                                        answerState[
                                                            option.value
                                                        ] === button.label
                                                            ? "rgb(175, 226, 62)"
                                                            : "rgb(218, 218, 218)",
                                                },
                                            }}
                                            onClick={() =>
                                                handleSelection(
                                                    option.value,
                                                    button.label
                                                )
                                            }
                                        >
                                            {button.label}
                                        </Button>
                                    ))}
                                </Box>
                            )
                        )}
                    </Box>
                </AccordionDetails>
            </CustomAccordion>

            {/* Modal */}
            <Modal
                open={modalOpen}
                onClose={handleClose}
                aria-labelledby="modal-title"
                aria-describedby="modal-description"
            >
                <Box
                    sx={{
                        position: "absolute",
                        top: {
                            xs: "50%",
                            sm: "48%",
                            md: "45%",
                            lg: "50%",
                        },
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: {
                            xs: "90%",
                            sm: "600px",
                            md: "750px",
                            lg: "900px",
                        },
                        maxWidth: "900px",
                        backgroundColor: "white",
                        boxShadow: 24,
                        padding: 0,
                        borderBottom: "10px solid rgb(0, 132, 180)",
                        overflowY: "auto",
                        maxHeight: "90vh",
                    }}
                >
                    {/* Modal Header */}
                    <Box
                        sx={{
                            backgroundColor: "rgb(0, 176, 240)",
                            color: "black",
                            padding: "8px 20px",
                            fontSize: "1.25em",
                            textAlign: "left",
                            borderBottom: "10px solid rgb(0, 132, 180)",
                            boxSizing: "border-box",
                            position: "relative",
                        }}
                    >
                        <Typography
                            id="modal-title"
                            variant="h1"
                            sx={{ fontSize: "30px", fontWeight: "700" }}
                        >
                            {modalContent?.title}
                        </Typography>
                        {/* Close Image */}
                        <Box component="img"
                            src={`${imagesResources}learning-l4q2a-5.png`}
                            alt="Close"
                            sx={{
                                position: "absolute",
                                top: "10px",
                                right: "10px",
                                cursor: "pointer",
                                width: "32px",
                                height: "32px",
                            }}
                            onClick={handleClose}
                        />
                    </Box>

                    {/* Modal Description */}
                    <Box sx={{ padding: "16px" }}>
                        <Typography
                            id="modal-description"
                            sx={{ fontSize: "24px", fontWeight: "700" }}
                        >
                            {modalContent?.details?.certificate}
                        </Typography>
                        <Typography
                            id="modal-description"
                            sx={{ fontSize: "24px" }}
                        >
                            {modalContent?.details?.description}
                        </Typography>
                        <Typography
                            my={2}
                            sx={{ fontSize: "24px", fontWeight: "700" }}
                        >
                            Delivery Methods:
                        </Typography>
                        <Box>
                            {modalContent?.details?.deliveryMethods.map(
                                (method, idx) => (
                                    <Box key={idx} sx={{ fontSize: "24px" }}>
                                        {method}
                                    </Box>
                                )
                            )}
                        </Box>
                        <Typography
                            my={2}
                            sx={{ fontSize: "24px", fontWeight: "700" }}
                        >
                            Start Dates:
                        </Typography>
                        <Box>
                            {modalContent?.details?.startDates.map(
                                (date, idx) => (
                                    <Box key={idx} sx={{ fontSize: "24px" }}>
                                        - {date}
                                    </Box>
                                )
                            )}
                        </Box>
                    </Box>
                </Box>
            </Modal>
        </Box>
    );
};